import { useState } from 'react';
import type { ManuscriptDocument } from '../../lib/pdf';
import type { PatternRunResult } from '../../lib/patterns';
import type { ReferenceAuditResult } from '../../lib/apis';
import {
  DECISION_LABELS,
  suggestDecision,
  explainDecision,
  type DecisionLabel,
} from '../../lib/export';

interface Props {
  manuscript: ManuscriptDocument | null;
  analysis: PatternRunResult | null;
  /** Folds retractions / metadata mismatches into the rubric (P0-2). */
  referenceAudit?: ReferenceAuditResult | null;
  decision: DecisionLabel | null;
  rationale: string;
  editorNotes: string;
  llmReady: boolean;
  onDecisionChange: (decision: DecisionLabel) => void;
  onRationaleChange: (rationale: string) => void;
  onNotesChange: (notes: string) => void;
  onDraftRationale: (decision: DecisionLabel) => Promise<string | null>;
}

const DECISION_COLOR: Record<DecisionLabel, string> = {
  desk_reject: 'bg-red-50 text-red-900 border-red-200',
  reject_with_feedback: 'bg-red-50 text-red-900 border-red-200',
  send_for_review_after_revisions: 'bg-amber-50 text-amber-900 border-amber-200',
  send_for_review: 'bg-emerald-50 text-emerald-900 border-emerald-200',
};

export function DecisionCard({
  manuscript,
  analysis,
  referenceAudit,
  decision,
  rationale,
  editorNotes,
  llmReady,
  onDecisionChange,
  onRationaleChange,
  onNotesChange,
  onDraftRationale,
}: Props) {
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);

  if (!manuscript || !analysis) {
    return (
      <section className="ms-card">
        <div className="ms-ribbon ms-ribbon--slate">
          <h2>Decision recommendation</h2>
        </div>
        <p className="text-xs leading-relaxed text-slate-600">
          A transparent rubric maps the findings — missing high-severity checklist items,
          integrity flags and reference retractions — to a suggested editorial action and
          shows its reasoning. This is advisory triage support, <strong>never a
          verdict</strong>: you set the final decision and rationale, and nothing is sent
          anywhere automatically.
        </p>
      </section>
    );
  }

  const suggested = suggestDecision(analysis, { referenceAudit });
  const effective = decision ?? suggested;
  const basis = explainDecision(analysis, { referenceAudit });

  async function draft() {
    if (!effective) return;
    setDrafting(true);
    setDraftError(null);
    try {
      const text = await onDraftRationale(effective);
      if (!text) setDraftError('Drafting failed — see browser console.');
    } catch (e) {
      setDraftError(e instanceof Error ? e.message : 'Draft failed.');
    } finally {
      setDrafting(false);
    }
  }

  return (
    <section className="ms-card">
      <div className="ms-ribbon ms-ribbon--slate">
        <h2>Decision recommendation</h2>
      </div>

      <div className="mt-2">
        <label className="text-[10px] uppercase tracking-wide text-slate-500">Decision</label>
        <select
          value={decision ?? suggested ?? ''}
          onChange={(e) => onDecisionChange(e.target.value as DecisionLabel)}
          className={`mt-1 w-full rounded border px-2 py-1 text-sm ${effective ? DECISION_COLOR[effective] : 'border-slate-200'}`}
        >
          <option value="" disabled>
            Select a decision…
          </option>
          {(Object.keys(DECISION_LABELS) as DecisionLabel[]).map((k) => (
            <option key={k} value={k}>
              {DECISION_LABELS[k]}
              {suggested === k ? ' (suggested)' : ''}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[10px] text-slate-500">{basis}</p>
      </div>

      <div className="mt-2">
        <div className="flex items-center justify-between gap-2">
          <label className="text-[10px] uppercase tracking-wide text-slate-500">Rationale</label>
          {llmReady && (
            <button
              type="button"
              disabled={!effective || drafting}
              onClick={draft}
              className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] text-slate-700 disabled:opacity-40"
            >
              {drafting ? 'Drafting…' : 'Draft with LLM'}
            </button>
          )}
        </div>
        <textarea
          value={rationale}
          onChange={(e) => onRationaleChange(e.target.value)}
          rows={4}
          placeholder="Why this recommendation? The chief editor reads this verbatim."
          className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs"
        />
        {draftError && <p className="mt-1 text-[10px] text-red-600">{draftError}</p>}
      </div>

      <div className="mt-2">
        <label className="text-[10px] uppercase tracking-wide text-slate-500">
          Editor notes (internal)
        </label>
        <textarea
          value={editorNotes}
          onChange={(e) => onNotesChange(e.target.value)}
          rows={2}
          placeholder="Notes for your own records — not sent to the chief editor by default."
          className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs"
        />
      </div>

      <p className="mt-2 text-[10px] text-slate-400">
        Advisory only. The editor&apos;s word is final.
      </p>
    </section>
  );
}
